import React, { useState, useEffect, memo } from 'react';
import { Box, Grid, Typography, IconButton, Toolbar, Drawer, useMediaQuery } from '@mui/material';
import { Menu as MenuIcon, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import PersonIcon from '@mui/icons-material/Person';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { KeyboardArrowRight as KeyboardArrowRightIcon } from '@mui/icons-material';
import SchoolIcon from '@mui/icons-material/School';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useNavigate } from 'react-router-dom';
import img from "../assets/qural1.png";
import { Outlet } from 'react-router-dom';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LogoutIcon from '@mui/icons-material/Logout';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GroupIcon from '@mui/icons-material/Group';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AddBoxIcon from '@mui/icons-material/AddBox';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupAddIcon from '@mui/icons-material/GroupAdd'; // Alternative for Add Lead
import MailOutlineIcon from '@mui/icons-material/MailOutline'; // Alternative for Follow-Up Lead
import SearchIcon from '@mui/icons-material/Search'; // Alternative for Search Lead
import { useLocation } from 'react-router-dom';


// Memoized Main component to prevent unnecessary re-renders
const Main = memo(
  styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open, drawerWidth }) => ({
      flexGrow: 1,
      padding: theme.spacing(3),
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      marginLeft: 0,
      marginTop: '75px',
      width: open ? `calc(100% - ${drawerWidth}px)` : '100%',
      ...(open && {
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
      }),
    })
  )
);

const username = localStorage.getItem('username');

// DrawerComponent to encapsulate drawer UI
const DrawerComponent = ({ drawerWidth, renderMenuItems, isMobile, theme, open, toggleDrawer }) => {
  const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
  }));

  return (
    <Drawer
      sx={{
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          maxWidth: '100vw',
          boxSizing: 'border-box',
          backgroundColor: '#111827',
          marginTop: '75px',
          height: 'calc(100vh - 75px)',
          overflowY: 'auto',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        },
      }}
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="left"
      open={open}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <DrawerHeader style={{ minHeight: '40px' }} sx={{ height: '10px' }}>
        <IconButton onClick={toggleDrawer} sx={{ height: '35px', color: 'white' }}>
          {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </DrawerHeader>
      <Box sx={{ padding: 2 }}>{renderMenuItems()}</Box>
    </Drawer>
  );
};

// HeaderBar component for the top bar
const HeaderBar = ({ currentDate, toggleDrawer }) => (
  <Grid
    container
    spacing={0}
    style={{
      background: 'linear-gradient(90deg, #254C9E 3.26%, #00AAEB 20.59%, #FFFFFF 78.42%)',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      zIndex: 1100,
      height: '75px',
    }}
  >
    <Grid
      item
      xs={1}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
    >
      <IconButton
        color="inherit"
        aria-label="open drawer"
        onClick={toggleDrawer}
        edge="start"
        sx={{ ml: 1 }}
      >
        <MenuIcon style={{ fontSize: '2.5rem', color: 'white' }} />
      </IconButton>
    </Grid>
    <Grid
      item
      xs={4}
      style={{ display: 'flex', justifyContent: 'start', alignItems: 'center', color: 'white' }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 550,
          fontSize: { xs: '15px', sm: '20px', md: '25px', lg: '20px' },
        }}
      >
        Hi, {username}
      </Typography>
    </Grid>
    <Grid
      item
      xs={6}
      style={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}
    >
      <img style={{ height: '80px', objectFit: 'contain' }} src={img} alt="Logo" />
    </Grid>
  </Grid>
);

export const Header = () => {
  const [currentDate, setCurrentDate] = useState('');
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const control = localStorage.getItem('control');
  const [expandedSection, setExpandedSection] = useState(null);
  const [activeSubheading, setActiveSubheading] = useState(null);
  const storedPath = localStorage.getItem('lastPath');
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const drawerWidth = isMobile ? '100%' : 300;
  const location = useLocation();


  const activeColor = '#00AAEB';
  const inactiveColor = 'white';
  const activeBgColor = '#ffffff14';
  const inactiveBgColor = 'transparent';

  const toggleDrawer = () => {
    setOpen(!open);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const date = new Date();
      const day = date.toLocaleDateString('en-US', { weekday: 'long' });
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const dayOfMonth = date.getDate();
      const year = date.getFullYear();
      const formattedDate = `${dayOfMonth < 10 ? '0' + dayOfMonth : dayOfMonth} ${month} ${year}, ${day}`;
      setCurrentDate(formattedDate);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const toggleSection = (sectionName, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setExpandedSection(prevSection => prevSection === sectionName ? null : sectionName);
  };

  const handleSectionClick = (event, sectionName) => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.preventDefault();
    event.nativeEvent.stopPropagation();
    toggleSection(sectionName, event);
    return false;
  };

  const menuItemStyle = (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    marginLeft: '8px',
    cursor: 'pointer',
    backgroundColor: isActive ? activeBgColor : inactiveBgColor,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      borderRadius: '4px',
    },
  });

  const navItemStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    marginLeft: '8px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      borderRadius: '4px',
    },
  };

  const submenuStyle = {
    marginLeft: '32px',
    width: 'calc(100% - 32px)',
  };

  const iconStyle = (isActive) => ({
    marginRight: '12px',
    color: isActive ? activeColor : inactiveColor,
  });

  const textStyle = (isActive) => ({
    color: isActive ? activeColor : inactiveColor,
  });

  const renderMenuItem = (icon, text, path) => {
    const isActive = activeSubheading === path;

    return (
      <Box
        sx={menuItemStyle(isActive)}
        onClick={() => {
          if (path) {
            navigate(path);
            setActiveSubheading(path);
            setOpen(false);
          }
        }}
      >
        {React.cloneElement(icon, { sx: iconStyle(isActive) })}
        <Typography sx={textStyle(isActive)}>{text}</Typography>
      </Box>
    );
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  const renderMenuItems = () => (
    <>
      <Typography variant="h6" color="white" gutterBottom sx={{ flexGrow: 1, fontSize: '20px' }}>
        Self Service
      </Typography>
      {renderMenuItem(<DashboardIcon />, 'Dashboard', '/dashboard')}
      {(control == 2 || control == 3 || control == 4) && renderMenuItem(<AccessTimeIcon />, 'Punch In', '/punch-in')}
      <Box sx={navItemStyle} onClick={(e) => handleSectionClick(e, 'attendance')}>
        {expandedSection === 'attendance' ? (
          <KeyboardArrowDownIcon sx={iconStyle(true)} />
        ) : (
          <KeyboardArrowRightIcon sx={iconStyle(false)} />
        )}
        <Typography color="white">Attendance Report</Typography>
      </Box>
      {expandedSection === 'attendance' && (
        <Box sx={submenuStyle}>
          {(control == 1 || control == 4 || control == 2) &&
            renderMenuItem(
              <PeopleAltIcon />,
              (control == 1 || control == 4) ? "Staff's Attendance" : 'My Attendance',
              '/staff-attendance'
            )}
          {renderMenuItem(
            <SchoolIcon />,
            (control == 1 || control == 4 || control == 2) ? "Student's Attendance" : 'My Attendance',
            '/student-attendance'
          )}
          {control == 1 && renderMenuItem(<PeopleAltIcon />, "Branch Admin's Attendance", '/branch-admin-attendance')}
        </Box>
      )}
      {(control == 1 || control == 4 || control == 2) && (
        <Box sx={navItemStyle} onClick={(e) => handleSectionClick(e, 'team')}>
          {expandedSection === 'team' ? (
            <KeyboardArrowDownIcon sx={iconStyle(true)} />
          ) : (
            <KeyboardArrowRightIcon sx={iconStyle(false)} />
          )}
          <Typography color="white">Team Members</Typography>
        </Box>
      )}
      {expandedSection === 'team' && (
        <Box sx={submenuStyle}>
          {(control == 1 || control == 4) && renderMenuItem(<PersonAddIcon />, 'Add Team Member', '/add-team-member')}
          {control == 1 && renderMenuItem(<PeopleAltIcon />, "Branch Admin's List", '/branchadminlist')}
          {(control == 1 || control == 4) && renderMenuItem(<PeopleAltIcon />, "Staff's List", '/stafflist')}
          {(control == 1 || control == 4 || control == 2) && renderMenuItem(<SchoolIcon />, "Student's List", '/studentlist')}
        </Box>
      )}
      {(control == 1 || control == 4 || control == 2) && (
        <Typography variant="h6" color="white" gutterBottom sx={{ flexGrow: 1, fontSize: '20px' }}>
          Courses
        </Typography>
      )}
      {(control == 1 || control == 4) && (
        <>
          <Box>{renderMenuItem(<MenuBookIcon />, 'All chapters', '/chapter')}</Box>
          <Box>{renderMenuItem(<MenuBookIcon />, 'All Module', '/module')}</Box>
          <Box>{renderMenuItem(<MenuBookIcon />, 'All courses', '/course')}</Box>
          <Box>{renderMenuItem(<AddCircleOutlineIcon />, 'Add Course', '/add-course')}</Box>
        </>
      )}
      <Box>{(control == 1 || control == 4 || control == 2) && renderMenuItem(<AddCircleOutlineIcon />, 'Batch', '/batch')}</Box>
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="h6" color="white" gutterBottom sx={{ flexGrow: 1, fontSize: '20px' }}>
          Task and Learning Journey
        </Typography>
        {(control == 2 || control == 3) && (
          <Box>
            {control == 2 && renderMenuItem(<AddBoxIcon />, 'Add Task', '/add-task')}
            {control == 3 && renderMenuItem(<ListAltIcon />, 'Add Learning Journey', '/add-learning-journey')}
            {(control == 1 || control == 4 || control == 2) &&
              renderMenuItem(
                <PeopleAltIcon />,
                (control == 1 || control == 4) ? "View Staff Task" : 'My Tasks',
                '/staff-task'
              )}
            {renderMenuItem(
              <SchoolIcon />,
              (control == 1 || control == 4 || control == 2) ? "View Student's Learning Journey" : 'My Learning Journey',
              '/student-learning-journey'
            )}
          </Box>
        )}
        {(control == 1 || control == 4) && (
          <Box>
            {control == 4 && renderMenuItem(<AddBoxIcon />, 'Add Task', '/add-task')}
            {renderMenuItem(<PeopleAltIcon />, "Staff's Task", '/staff-task')}
            {renderMenuItem(<SchoolIcon />, "Student's Learning Journey", '/student-learning-journey')}
            {control == 1 && renderMenuItem(<PeopleAltIcon />, "Branch Admin's Task", '/branch-admin-task')}
          </Box>
        )}
      </Box>
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="h6" color="white" gutterBottom sx={{ flexGrow: 1, fontSize: '20px' }}>
          Assignments
        </Typography>
        {(control == 1 || control == 4 || control == 2) && renderMenuItem(<AddCircleOutlineIcon />, 'Create Assignment', '/assignment')}
        {(control == 1 || control == 4 || control == 2) && renderMenuItem(<VisibilityIcon />, 'View Assignments', '/view-assignment')}
        {control == 3 && renderMenuItem(<AssignmentIcon />, 'Assignment', '/submit-assignment')}
      </Box>
      {(control == 1 || control == 4) && (
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" color="white" gutterBottom sx={{ flexGrow: 1, fontSize: '20px' }}>
            Lead Management
          </Typography>
          {(control == 1 || control == 4) && (
            <>
              <Box>{renderMenuItem(<GroupAddIcon />, 'Add Lead', '/add-lead')}</Box>
              <Box>{renderMenuItem(<MailOutlineIcon />, 'Follow-Up Lead', '/followup-lead')}</Box>
              <Box>{renderMenuItem(<SearchIcon />, 'Search Lead', '/search-lead')}</Box>
            </>
          )}
        </Box>
      )}
      {(control == 1 || control == 4 || control == 2) && ( // Modified condition to include control == 0
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" color="white" gutterBottom sx={{ flexGrow: 1, fontSize: '20px' }}>
            Assessments
          </Typography>
          <Box>{renderMenuItem(<MenuBookIcon />, 'Assessments', '/assessment')}</Box>
          {(control == 1) && (
            <>
              <Box>{renderMenuItem(<VisibilityIcon />, 'Pending Requests', '/admin/requests')}</Box>
              <Box>{renderMenuItem(<VisibilityIcon />, 'Edit History', '/admin/edit-history')}</Box>
            </>
          )}
        </Box>
      )}
      {(control == 1 || control == 4) && (
        <Box sx={{ marginBottom: 3 }}>
           <Typography variant="h6" color="white" gutterBottom sx={{ flexGrow: 1, fontSize: '20px' }}>
            Certificate
          </Typography>
          <Box>{renderMenuItem(<MailOutlineIcon />, 'Generate Certificate', '/generate-certificate')}</Box>
          <Box>{renderMenuItem(<SearchIcon />, 'View Certificates', '/generated-certificate')}</Box>
        </Box>
      )}
      <Box sx={{ marginTop: 4, borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: 2 }}>
        {(control == 2 || control == 3) && renderMenuItem(<AccountCircleIcon />, 'Profile', '/view-profile')}
        <Box sx={navItemStyle} onClick={handleLogout}>
          <LogoutIcon sx={iconStyle(false)} />
          <Typography color={inactiveColor}>Logout</Typography>
        </Box>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <HeaderBar currentDate={currentDate} toggleDrawer={toggleDrawer} />
      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        <DrawerComponent
          drawerWidth={drawerWidth}
          renderMenuItems={renderMenuItems}
          isMobile={isMobile}
          theme={theme}
          open={open}
          toggleDrawer={toggleDrawer}
        />
        <Main key={location.pathname} open={open} drawerWidth={drawerWidth} sx={{ padding: '0px', paddingBottom: '0px' }}>
          <Outlet />
        </Main>
      </Box>
    </Box>
  );
};
